import { useAuth0 } from '@auth0/auth0-react';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { publicRoutes, routes } from './routing/Routes';
import Shell from './Components/Shell/Shell';
import { IRoute } from './routing/types';
import { useAppDispatch, setToken } from './store';
import theme from './Theme';
import jwt_decode from "jwt-decode";
import { LoadingIndicator, NotAuthorizedError, userHasPermissions, UserPermissionContext } from './Components/CoreLib/library';
import { clarity } from 'clarity-js';

function App() {
    const auth = useAuth0();
    const dispatch = useAppDispatch();
    const [accessTokenSet, setAccessTokenSet] = useState(false);
    const [userPermissions, setUserPermissions] = useState<string[] | null>(null);
    useEffect(() => {
        let clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
        if (auth.isAuthenticated && clarityProjectId) {
            clarity.consent()
            clarity.identify(auth.user?.email ?? "")

            clarity.start({
                projectId: clarityProjectId,
                upload: 'https://m.clarity.ms/collect',
                track: true,
                content: true,
            });
            return () => {
                clarity.stop()
            }

        }
    }, [auth]);

    useEffect(() => {
        (async () => {
            if (auth.isAuthenticated) {
                if (!userPermissions) {
                    const accessToken = await auth.getAccessTokenSilently({
                        authorizationParams: {
                            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                            redirect_uri: window.location.origin,
                            scope: "SCOPE profile email offline_access",
                        }
                    });
                    const decodedToken = jwt_decode(accessToken);
                    const permissions = ((decodedToken as any)?.permissions as string[]) ?? [];
                    setUserPermissions(permissions);
                    dispatch(setToken(accessToken));
                    setAccessTokenSet(true);
                }
            }
        })();
    }, [userPermissions, setUserPermissions, auth, dispatch]);

    if (auth.isLoading || (auth.isAuthenticated && !accessTokenSet)) {
        return <LoadingIndicator />;
    }

    if (auth.error) {
        return <p>{auth.error.message}</p>;
    }

    const renderRoute = (isPublic: boolean) => (route: IRoute) => {
        if (isPublic || (userPermissions && userHasPermissions(route.requiredPermissions ?? [], userPermissions))) {
            return <Route key={route.path} path={route.path} element={route.component} />;
        } else {
            return <Route key={route.path} path={route.path} element={<NotAuthorizedError />} />;
        }
    };

    const togglePermission = (permission: string): void => {
        if (!userPermissions) {
            return;
        }
        const isPermissionActive = userPermissions.includes(permission);
        if (isPermissionActive) {
            setUserPermissions([...userPermissions.filter(p => p !== permission)]);
        } else {
            setUserPermissions([...userPermissions, permission]);
        }
    }

    return (
        <BrowserRouter>
            <ThemeProvider theme={theme}>
                <CssBaseline>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <SnackbarProvider maxSnack={3} preventDuplicate>
                            <UserPermissionContext.Provider value={{
                                permissions: userPermissions ?? [],
                                togglePermission: togglePermission,
                                clearPermissions: () => {
                                    setUserPermissions(null);
                                    setAccessTokenSet(false);
                                },
                            }}>
                                <Routes>
                                    <Route path='/' element={<Shell isProtected={true} />}>
                                        {routes.map(renderRoute(false))}
                                    </Route>
                                    <Route path='/' element={<Shell />}>
                                        {publicRoutes.map(renderRoute(true))}
                                    </Route>
                                </Routes>
                            </UserPermissionContext.Provider>
                        </SnackbarProvider>
                    </LocalizationProvider>
                </CssBaseline>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
